import Block from '../block'

import gsap from 'gsap'

export default class CascadingSlider extends Block {
	static title = 'cascading-slider'

	constructor($el) {
		super($el)

		this.initCascadingSlider(this.$el)
	}

	initCascadingSlider($wrapper) {
		const reduced = window.matchMedia(
			'(prefers-reduced-motion: reduce)',
		).matches
		const duration = reduced ? 0 : 0.65
		const ease = 'power3.inOut'
		const contentFadeInDelay = 0.5

		const breakpoints = [
			{ maxWidth: 479, activeWidth: 0.78, siblingWidth: 0.08 },
			{ maxWidth: 767, activeWidth: 0.7, siblingWidth: 0.1 },
			{ maxWidth: 991, activeWidth: 0.6, siblingWidth: 0.1 },
			{ maxWidth: Infinity, activeWidth: 0.54, siblingWidth: 0.11 },
		]

		setupInstance($wrapper)

		function setupInstance($wrapper) {
			const $viewport = $wrapper.find('[data-cascading-viewport]')
			const $prevButton = $wrapper.find('[data-cascading-slider-prev]')
			const $nextButton = $wrapper.find('[data-cascading-slider-next]')
			const $content = $wrapper.find('[data-cascading-content]')
			const $contentTitle = $wrapper.find('[data-cascading-title]')
			const $contentText = $wrapper.find('[data-cascading-text]')
			let $slides = $viewport.find('[data-cascading-slide]')

			let totalSlides = $slides.length

			if (!totalSlides) return

			// Clone slides until minimum
			if (totalSlides < 9) {
				const $originalSlides = $slides.clone(true)

				while ($slides.length < 9) {
					$originalSlides.each(function () {
						const $clone = $(this).clone(true)

						$clone.attr('data-clone', '')
						$viewport.append($clone)

						$slides = $viewport.find('[data-cascading-slide]')
					})
				}

				totalSlides = $slides.length
			}

			let activeIndex = 0
			let isAnimating = false
			let slideWidth = 0
			let slotCenters = {}
			let slotWidths = {}

			function readGap() {
				const raw = getComputedStyle($viewport[0])
					.getPropertyValue('--gap')
					.trim()

				if (!raw) return 0

				const $temp = $('<div>')
					.css({
						width: raw,
						position: 'absolute',
						visibility: 'hidden',
					})
					.appendTo($viewport)

				const px = $temp.outerWidth()

				$temp.remove()

				return px
			}

			function getSettings() {
				const windowWidth = window.innerWidth

				for (let i = 0; i < breakpoints.length; i++) {
					if (windowWidth <= breakpoints[i].maxWidth) {
						return breakpoints[i]
					}
				}

				return breakpoints[breakpoints.length - 1]
			}

			function getOffset(slideIndex, fromIndex = activeIndex) {
				let distance = slideIndex - fromIndex
				const half = totalSlides / 2

				if (distance > half) distance -= totalSlides
				if (distance < -half) distance += totalSlides

				return distance
			}

			function measure() {
				const settings = getSettings()
				const viewportWidth = $viewport.outerWidth()
				const gap = readGap()

				const activeSlideWidth = viewportWidth * settings.activeWidth

				const siblingSlideWidth = viewportWidth * settings.siblingWidth

				const farSlideWidth = Math.max(
					0,
					(viewportWidth - activeSlideWidth - 2 * siblingSlideWidth - 4 * gap) /
						2,
				)

				slideWidth = activeSlideWidth

				const visibleSlots = [
					{ slot: -2, width: farSlideWidth },
					{ slot: -1, width: siblingSlideWidth },
					{ slot: 0, width: activeSlideWidth },
					{ slot: 1, width: siblingSlideWidth },
					{ slot: 2, width: farSlideWidth },
				]

				let x = 0

				$.each(visibleSlots, function (i, def) {
					slotCenters[String(def.slot)] = x + def.width / 2
					slotWidths[String(def.slot)] = def.width

					if (i < visibleSlots.length - 1) {
						x += def.width + gap
					}
				})

				slotCenters['-3'] =
					slotCenters['-2'] - farSlideWidth / 2 - gap - farSlideWidth / 2

				slotWidths['-3'] = farSlideWidth

				slotCenters['3'] =
					slotCenters['2'] + farSlideWidth / 2 + gap + farSlideWidth / 2

				slotWidths['3'] = farSlideWidth

				$slides.css('width', slideWidth + 'px')

				if ($content.length) {
					$content.css('width', slideWidth + 'px')
				}
			}

			function getSlideProps(offset) {
				const clamped = Math.max(-3, Math.min(3, offset))

				const slotWidth = slotWidths[String(clamped)]

				const clipAmount = Math.max(0, (slideWidth - slotWidth) / 2)

				const translateX = slotCenters[String(clamped)] - slideWidth / 2

				return {
					'x': translateX,
					'--clip': clipAmount,
					'zIndex': 10 - Math.abs(clamped),
				}
			}

			function layout(animate, previousIndex) {
				$slides.each(function (index) {
					const slide = this
					const offset = getOffset(index)

					if (offset < -3 || offset > 3) {
						if (animate && previousIndex !== undefined) {
							const previousOffset = getOffset(index, previousIndex)

							if (previousOffset >= -2 && previousOffset <= 2) {
								const exitSlot = previousOffset < 0 ? -3 : 3

								gsap.to(
									slide,
									$.extend({}, getSlideProps(exitSlot), {
										duration,
										ease,
										overwrite: true,
									}),
								)

								return
							}
						}

						const parkSlot = offset < 0 ? -3 : 3

						gsap.set(slide, getSlideProps(parkSlot))

						return
					}

					const props = getSlideProps(offset)

					$(slide).attr('data-status', offset === 0 ? 'active' : 'inactive')

					if (animate) {
						gsap.to(
							slide,
							$.extend({}, props, {
								duration,
								ease,
								overwrite: true,
							}),
						)
					} else {
						gsap.set(slide, props)
					}
				})
			}

			function updateContent() {
				if (!$contentTitle.length && !$contentText.length) return

				const $active = $slides.eq(activeIndex)

				$contentTitle.text($active.attr('data-title') || '')
				$contentText.text($active.attr('data-caption') || '')
				$contentText.scrollTop(0)
			}

			function transitionContent() {
				if (!$content.length || reduced) return updateContent()

				gsap.to($content[0], {
					opacity: 0,
					duration: 0.2,
					ease: 'power1.out',
					overwrite: true,
					onComplete: function () {
						updateContent()

						gsap.to($content[0], {
							opacity: 1,
							duration: 0.3,
							delay: contentFadeInDelay,
							ease: 'power1.out',
							overwrite: true,
						})
					},
				})
			}

			function goTo(targetIndex) {
				const normalizedTarget =
					((targetIndex % totalSlides) + totalSlides) % totalSlides

				if (isAnimating || normalizedTarget === activeIndex) return

				isAnimating = true

				const previousIndex = activeIndex

				const travelDirection =
					getOffset(normalizedTarget, previousIndex) > 0 ? 1 : -1

				$slides.each(function (index) {
					const slide = this

					const currentOffset = getOffset(index, previousIndex)

					const nextOffset = getOffset(index, normalizedTarget)

					const wasInRange = currentOffset >= -3 && currentOffset <= 3

					const willBeVisible = nextOffset >= -2 && nextOffset <= 2

					if (!wasInRange && willBeVisible) {
						const entrySlot = travelDirection > 0 ? 3 : -3

						gsap.set(slide, getSlideProps(entrySlot))
					}

					const wasInvisible = Math.abs(currentOffset) >= 3

					const willBeStaging = Math.abs(nextOffset) === 3

					const crossesSides = currentOffset * nextOffset < 0

					if (wasInvisible && willBeStaging && crossesSides) {
						gsap.set(slide, getSlideProps(nextOffset > 0 ? 3 : -3))
					}
				})

				activeIndex = normalizedTarget

				layout(true, previousIndex)
				transitionContent()

				gsap.delayedCall(duration + 0.05, function () {
					isAnimating = false
				})
			}

			$prevButton.on('click', function () {
				goTo(activeIndex - 1)
			})

			$nextButton.on('click', function () {
				goTo(activeIndex + 1)
			})

			$slides.on('click', function () {
				const index = $slides.index(this)

				if (index !== activeIndex) {
					goTo(index)
				}
			})

			// only the slider that has focus answers, so arrows in a field or another slider stay theirs
			$wrapper.on('keydown', function (event) {
				if ($(event.target).is('input, textarea, select, [contenteditable]')) {
					return
				}

				if (event.key === 'ArrowLeft') {
					goTo(activeIndex - 1)
				}

				if (event.key === 'ArrowRight') {
					goTo(activeIndex + 1)
				}
			})

			let resizeTimer

			$(window).on('resize', function () {
				clearTimeout(resizeTimer)

				resizeTimer = setTimeout(function () {
					measure()
					layout(false)
				}, 100)
			})

			measure()
			layout(false)
			updateContent()
		}
	}
}
